'use client';

import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { ExternalLink, X, Share2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import ShareYarnSheet from './ShareYarnSheet';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { db } from '@/lib/db';
import { formatDate } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import type { Yarn, Project, ProjectNeedle } from '@/lib/types';
import { yarnMatchesLibWithFallback } from '@/lib/yarnIdentity';
import { totalOwnedSkeins, claimedSkeins, formatSkeinCount } from '@/lib/skeinFormat';
import { safeHref } from '@/lib/safeUrl';
import YarnSwatch from './YarnSwatch';
import { useMeasurementSystem, formatYardage, formatNeedleSize, convertGaugeCount } from '@/lib/units';
import Modal from './Modal';
import YarnForm from './YarnForm';
import BlobImage from './BlobImage';
import ProjectNeedleDetailModal from './ProjectNeedleDetailModal';
import { Card } from './ui/Card';
import { CardSectionHeader } from './ui/CardSectionHeader';
import { Chip } from './ui/Chip';
import { DashedAddButton } from './ui/DashedAddButton';
import { DetailRow } from './ui/DetailRow';
import { ItalicBody } from './ui/ItalicBody';
import { MonoLabel } from './ui/MonoLabel';
import { ZineButton } from './ui/ZineButton';

interface YarnDetailModalProps {
  yarn: Yarn;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Caller opens a pre-filled "new yarn" form with brand/fiber/weight etc.
   *  copied from this yarn, leaving color-specific fields blank. Only the
   *  /yarn list page wires this — the project-side usages omit it. */
  onDuplicateColorway?: () => void;
}

export default function YarnDetailModal({ yarn: yarnProp, onClose, onEdit, onDelete, onDuplicateColorway }: YarnDetailModalProps) {
  const router = useRouter();
  const { t } = useI18n();
  // Keep the displayed yarn fresh from the library so the in-place Edit
  // below reflects immediately after saving. Falls back to the passed-in
  // yarn for snapshots that aren't a persisted library row (no id).
  const liveYarn = useLiveQuery(
    async () => (yarnProp.id != null ? await db.yarns.get(yarnProp.id) : undefined),
    [yarnProp.id],
  );
  const yarn = liveYarn ?? yarnProp;
  // In-place edit (Veronika 2026-06-08): every place the card opens gets a
  // "classic" Edit button at the bottom, so notes can be added without
  // hunting for the yarn in the stash. The /yarn page passes its own onEdit
  // and keeps its existing flow; everywhere else self-edits here.
  const [editing, setEditing] = useState(false);
  const measurementSystem = useMeasurementSystem();
  const projects = useLiveQuery(async () => (await db.projects.toArray()).filter(p => p.deletedAt == null));
  // Used by the matcher's sibling fallback below — tells us whether
  // this yarn is the unambiguous resolution target for project refs
  // that left their color blank.
  const liveLibrary = useLiveQuery(
    async () => (await db.yarns.toArray()).filter(y => y.deletedAt == null),
    [],
    [] as Yarn[],
  );
  const [viewProject, setViewProject] = useState<Project | null>(null);
  const [nestedYarn, setNestedYarn] = useState<Yarn | null>(null);
  const [nestedNeedle, setNestedNeedle] = useState<ProjectNeedle | null>(null);
  const [lightbox, setLightbox] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const openYarnFromChip = async (yarnName: string) => {
    if (yarnName === yarn.name) { setViewProject(null); return; }
    const match = await db.yarns.where('name').equals(yarnName).first();
    if (match) setNestedYarn(match);
  };

  // Match project-side yarn references to this library colorway.
  // Strict colorway match plus a blank-color sibling fallback so old
  // project refs typed without color data still count against the
  // colorway they render as — see yarnMatchesLibWithFallback.
  const matches = (y: { yarnName: string; colorName?: string; colorNumber?: string }) =>
    yarnMatchesLibWithFallback(y, yarn, liveLibrary);

  const usedIn = (projects || []).filter(p => p.yarns?.some(matches));
  const reviews = (projects || [])
    .flatMap(p => (p.yarns || []).filter(y => matches(y) && y.review).map(y => ({ projectName: p.name, review: y.review! })));

  // Inventory calculation — per project breakdown.
  // A project counts toward this yarn's "in progress" total when it
  // claims any whole skeins or any partial-skein fraction. Grams-based
  // partials only convert to a skein-scale value when this yarn knows
  // its grams-per-skein, otherwise they're treated as zero on the
  // skein math (the tag still shows so the user knows it's allocated).
  const projectYarnSkeins = (y: Parameters<typeof claimedSkeins>[0]) => claimedSkeins(y, yarn.gramsPerSkein);
  const inProgressProjects = (projects || [])
    .filter(p => (p.status === 'in-progress' || p.status === 'hibernating') && p.yarns?.some(matches))
    .map(p => ({
      id: p.id,
      name: p.name,
      skeins: (p.yarns || []).filter(matches).reduce((s, y) => s + projectYarnSkeins(y), 0),
    }))
    .filter(p => p.skeins > 0);
  const inProgressSkeins = inProgressProjects.reduce((sum, p) => sum + p.skeins, 0);
  // Finished projects no longer contribute to the yarn's available
  // math — the skeins were physically consumed when the project was
  // marked finished and yarn stock already dropped at that moment.
  const totalOwned = totalOwnedSkeins(yarn);
  const rawAvailable = totalOwned - inProgressSkeins;
  const availableSkeins = Math.max(0, rawAvailable);
  const isOverAllocated = rawAvailable < 0;
  const overAllocatedBy = Math.abs(rawAvailable);

  // Future project plans — don't consume stock, shown as separate info
  const futureProjects = (projects || [])
    .filter(p => p.status === 'future' && p.yarns?.some(matches))
    .map(p => ({
      id: p.id,
      name: p.name,
      skeins: (p.yarns || []).filter(matches).reduce((s, y) => s + projectYarnSkeins(y), 0),
    }))
    .filter(p => p.skeins > 0);
  const futurePlannedSkeins = futureProjects.reduce((sum, p) => sum + p.skeins, 0);

  // 2026-05-09 build 1251 — Veronika 'how about if the card opens
  // as window and it doesn't have a white background'. Bypass the
  // shared Modal component (which renders a white card) and use a
  // custom B1 zine modal frame: backdrop + cream-paper-with-halftone
  // panel. Build 1265 — the masthead now uses the swatch + name +
  // brand row instead of a split-title text masthead, so the
  // detailTitleHead/Tail helpers are no longer needed.

  return (
    <div
      // 2026-05-10 build 1263 — Veronika clarified "floating": a
      // centered pop-up with the page background visible around it,
      // not a full-screen sheet. Was `items-end sm:items-center`
      // (bottom-sheet on mobile, growing to 95vh and clipping under
      // the iPhone status bar so the title disappeared); now centered
      // on every breakpoint with safe-area padding all around so the
      // panel sits in a "window" with cream halftone showing on the
      // sides + top + bottom.
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{
        padding: 'max(env(safe-area-inset-top, 0px), 24px) 16px max(env(safe-area-inset-bottom, 0px), 24px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-text-primary/20 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg max-h-[80vh] flex flex-col"
        style={{
          zIndex: 60,
          backgroundColor: 'var(--paper-1)',
          backgroundImage: 'radial-gradient(circle, rgba(15,14,12,0.10) 1px, transparent 1.4px)',
          backgroundSize: '4px 4px',
          border: '1px solid var(--ink)',
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* 2026-05-10 build 1265 — Veronika spotted the yarn name
            duplication: it appeared once in the masthead title bar
            and again in the swatch+name+brand row below the photo.
            She circled the bottom row and said "the [content] in
            red square can be the head of the card". So the
            masthead now IS the swatch + name + brand row, and the
            duplicate inside the body is removed below. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 14px 12px 12px',
            borderBottom: '1px solid var(--ink)',
            flexShrink: 0,
            gap: 12,
          }}
        >
          <YarnSwatch
            yarn={yarn}
            ariaLabel={yarn.colorName}
            className="w-12 h-12 overflow-hidden"
            style={{ border: '1px solid var(--ink)', flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                lineHeight: 1,
                color: 'var(--ink)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {yarn.name}
            </h2>
            {yarn.brand && (
              <p
                style={{
                  // 2026-05-10 build 1266 — Veronika: brand name in
                  // the masthead reads better in our italic font
                  // (Fraunces) than mono caps. Lowercase italic
                  // matches the rest of the editorial copy in the
                  // app (price, notes, shop link).
                  margin: '3px 0 0',
                  fontFamily: "'Fraunces', 'Times New Roman', serif",
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  color: 'var(--ink-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {yarn.brand.toLowerCase()}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: 32,
              height: 32,
              border: '1px solid var(--ink)',
              background: 'transparent',
              color: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 pb-20 flex-1">
      {/* 2026-05-10 build 1262 — Veronika: "Can the yarn cards be
          more floating?" Bumped gap from space-y-4 (16) to
          space-y-5 (20) so the bordered blocks breathe more,
          and added a subtle hard-edge offset shadow to each
          B1 zine block via the shared `cardLiftShadow` const
          applied below. */}
      <div className="space-y-5">
        {/* Photo — click to open a fullscreen lightbox */}
        {yarn.photo && (
          <button
            type="button"
            onClick={() => setLightbox(true)}
            aria-label="Open yarn photo"
            className="block w-full cursor-zoom-in"
          >
            <BlobImage src={yarn.photo} className="w-full h-40 object-cover" style={{ border: '1px solid var(--ink)' }} />
          </button>
        )}

        {/* Inventory breakdown — v5 (2026-05-10 build 1306).
            Veronika after v4 (compact rows): "Somehow I still
            don't like the inventory. Is there easier more simple
            way how to communicate the inventory numbers?" Pivoted
            from a bordered table to an editorial sentence: a
            small mono-caps INVENTORY anchor + one sans line
            "{N} skeins · {M} available" + a Fraunces-italic
            footnote listing in-progress allocations. No box, no
            row dividers, no header strip. Reads as one
            statement; the whole inventory becomes a paragraph
            in the modal flow. */}
        {(() => {
          const whole = yarn.skeinCount || 0;
          const fraction = yarn.partialSkein || 0;
          const partialGrams = yarn.partialGrams || 0;
          const totalSkeinPortion = whole + fraction;
          const totalLabel = totalSkeinPortion === 1 ? t.skein : t.skeins;
          const sansFamily = 'var(--font-geist-sans), system-ui, sans-serif';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: sansFamily,
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: '-0.005em',
                  color: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  columnGap: 8,
                  rowGap: 2,
                }}
              >
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatSkeinCount(totalSkeinPortion)}{partialGrams > 0 ? ` +${partialGrams}g` : ''} {totalLabel.toLowerCase()}
                </span>
                <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}>
                  · {formatSkeinCount(availableSkeins, yarn.gramsPerSkein)} {(t.available as string).toLowerCase()}
                </span>
              </p>
              {inProgressProjects.length > 0 && (
                /* 2026-05-10 build 1307 — Veronika: "Its actually
                   better but too many different fonts." Inventory
                   was rendering in mono + sans + Fraunces italic.
                   Dropped the Fraunces italic for the footnote;
                   in-progress allocations are now in sans medium
                   (same family as the headline above), matching
                   the mono-label + sans-value rhythm of the
                   FIBER / WEIGHT / COLOR rows. Two fonts in this
                   block instead of three. */
                <p
                  style={{
                    margin: 0,
                    fontFamily: sansFamily,
                    fontWeight: 500,
                    fontSize: 13,
                    lineHeight: 1.45,
                    color: 'var(--ink-2)',
                    textAlign: 'right',
                  }}
                >
                  {inProgressProjects.map((p, idx) => (
                    <span key={p.id}>
                      {idx > 0 && ', '}
                      {`${formatSkeinCount(p.skeins, yarn.gramsPerSkein)} ${p.skeins !== 1 ? t.skeins : t.skein} → `}
                      <Chip
                        onClick={() => setViewProject(projects?.find(pr => pr.id === p.id) || null)}
                        style={{ verticalAlign: 'middle', display: 'inline-block' }}
                      >
                        {p.name}
                      </Chip>
                    </span>
                  ))}
                </p>
              )}
              {isOverAllocated && (
                <p
                  style={{
                    margin: '4px 0 0',
                    fontFamily: sansFamily,
                    fontWeight: 600,
                    fontSize: 12,
                    color: 'var(--over-num)',
                    lineHeight: 1.4,
                    padding: '6px 10px',
                    background: '#FBE9E9',
                    border: '1px solid #E40000',
                    textAlign: 'right',
                  }}
                >
                  ⚠ {t.yarnOverAllocated
                    .replace('{over}', formatSkeinCount(overAllocatedBy, yarn.gramsPerSkein))
                    .replace('{total}', formatSkeinCount(inProgressSkeins, yarn.gramsPerSkein))
                    .replace('{owned}', formatSkeinCount(totalOwned, yarn.gramsPerSkein))}
                </p>
              )}
            </div>
          );
        })()}

        {/* Details — bordered card, mono labels + display caps values */}
        <Card>
          {yarn.fiber && <DetailRow label={t.fiber} value={yarn.fiber} />}
          {yarn.weight && <DetailRow label={t.weight} value={yarn.weight} />}
          {/* Show the COLOR row when EITHER the name or the number is filled.
              Was `yarn.colorName &&` — a colorway entered with only a number
              (no name) rendered nothing, hiding data the user typed. (Veronika
              2026-07-01: "Cardiff — I filled number of color but not name, but
              I don't see number of color on the card.") */}
          {(yarn.colorName || yarn.colorNumber) && (
            <DetailRow
              label={t.color}
              value={yarn.colorName
                ? `${yarn.colorName}${yarn.colorNumber ? ` #${yarn.colorNumber}` : ''}`
                : `#${yarn.colorNumber}`}
            />
          )}
          {yarn.yardage > 0 && <DetailRow label={t.lengthPerSkein} value={formatYardage(yarn.yardage, measurementSystem)} />}
          {yarn.gramsPerSkein && yarn.gramsPerSkein > 0 && <DetailRow label={t.weightPerSkein} value={`${yarn.gramsPerSkein}g`} />}
          {yarn.recommendedNeedles && <DetailRow label={t.recommendedNeedles} value={yarn.recommendedNeedles} />}
          {(yarn.mfgGaugeStitches || yarn.mfgGaugeRows) && (
            <DetailRow
              label={t.mfgGauge}
              sublabel={t.gaugePer10cm}
              value={`${yarn.mfgGaugeStitches ? convertGaugeCount(yarn.mfgGaugeStitches, measurementSystem) : '—'}s × ${yarn.mfgGaugeRows ? convertGaugeCount(yarn.mfgGaugeRows, measurementSystem) : '—'}r${yarn.mfgGaugeNeedle ? ` · ${formatNeedleSize(yarn.mfgGaugeNeedle, measurementSystem)}` : ''}`}
            />
          )}
          {(yarn.myGaugeStitches || yarn.myGaugeRows) && (
            <DetailRow
              label={t.myGauge}
              sublabel={t.gaugePer10cm}
              value={`${yarn.myGaugeStitches ? convertGaugeCount(yarn.myGaugeStitches, measurementSystem) : '—'}s × ${yarn.myGaugeRows ? convertGaugeCount(yarn.myGaugeRows, measurementSystem) : '—'}r${yarn.myGaugeNeedle ? ` · ${formatNeedleSize(yarn.myGaugeNeedle, measurementSystem)}` : ''}`}
            />
          )}
        </Card>

        {/* Future plans — doesn't consume stock, informational only.
            2026-05-19 build 1477 (Veronika msg 4215-4216): ported from
            the pre-zine rounded-pill recipe to the same Card +
            CardSectionHeader vocabulary as "Your review" right below. */}
        {futureProjects.length > 0 && (
          <Card>
            <CardSectionHeader>{t.plannedForFuture}</CardSectionHeader>
            <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: 'var(--ink-2)',
                }}
              >
                {t.plannedForFutureSummary
                  .replace('{n}', String(futurePlannedSkeins))
                  .replace('{unit}', futurePlannedSkeins !== 1 ? t.skeins : t.skein)}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {futureProjects.map(p => (
                  <div
                    key={p.id}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                  >
                    <Chip onClick={() => setViewProject(projects?.find(pr => pr.id === p.id) || null)}>
                      {p.name}
                    </Chip>
                    <span
                      style={{
                        fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--ink)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {p.skeins} {p.skeins !== 1 ? t.skeins : t.skein}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Your review — B1 zine bordered block; mono caps header
            with hairline divider, Fraunces italic body for the
            handwritten/personal voice (2026-05-10). */}
        {yarn.review && (
          <Card>
            <CardSectionHeader>{t.yourReview}</CardSectionHeader>
            <ItalicBody size="md" style={{ fontSize: 13, padding: '10px 12px 12px' }}>
              {yarn.review}
            </ItalicBody>
          </Card>
        )}

        {/* Reviews from projects */}
        {reviews.length > 0 && (
          <div>
            <p className="text-[10px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">{t.reviewsFromProjects}</p>
            {reviews.map((r, i) => (
              <div key={i} className="p-2.5 bg-bg-elevated rounded-lg mb-1.5">
                <p className="text-[10px] font-medium text-text-secondary">{r.projectName}</p>
                <p className="text-xs text-text-primary mt-0.5 leading-relaxed">{r.review}</p>
              </div>
            ))}
          </div>
        )}

        {/* Used in projects — project chips now use the canonical
            B1 zine tag style (1px ink border, cream paper bg, mono caps,
            ink-secondary text), matching the FIBER/WEIGHT/COLOR tag chips
            elsewhere. (2026-05-10) */}
        {usedIn.length > 0 && (
          <div>
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--ink-2)',
                margin: '0 0 6px',
              }}
            >
              {t.usedIn}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {usedIn.map(p => (
                <Chip
                  key={p.id}
                  onClick={() => setViewProject(projects?.find(pr => pr.id === p.id) || null)}
                >
                  {p.name}
                </Chip>
              ))}
            </div>
          </div>
        )}

        {/* Shop link — scheme-validated; refuses javascript:/data:text/html
            URIs. Editorial styling: Fraunces italic, ink color, underlined,
            external-link icon trailing the text (2026-05-10). */}
        {yarn.shopUrl && (() => {
          const href = safeHref(yarn.shopUrl);
          if (!href) return null;
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: "'Fraunces', 'Times New Roman', serif",
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 13,
                color: 'var(--ink)',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                alignSelf: 'flex-start',
              }}
            >
              {t.shopLink} <ExternalLink size={13} strokeWidth={1.5} />
            </a>
          );
        })()}

        {/* Purchase price — subtle inline note, left-aligned */}
        {yarn.pricePerSkein && yarn.pricePerSkein > 0 && (() => {
          const currency = yarn.priceCurrency || '';
          const perMeter = yarn.yardage && yarn.yardage > 0
            ? (yarn.pricePerSkein / yarn.yardage).toFixed(2)
            : null;
          const perGram = yarn.gramsPerSkein && yarn.gramsPerSkein > 0
            ? (yarn.pricePerSkein / yarn.gramsPerSkein).toFixed(2)
            : null;
          // Use Fraunces italic — the same editorial body font already
          // used elsewhere in the app — to keep the modal to its three
          // canonical families (Fraunces / Geist Sans / JetBrains Mono).
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ItalicBody>
                {t.pricePerSkein}: {yarn.pricePerSkein} {currency} · {t.pricePerSkeinHint}
              </ItalicBody>
              {(perMeter || perGram) && (
                <ItalicBody>
                  {perMeter && <>1 m ≈ {perMeter} {currency}</>}
                  {perMeter && perGram && ' · '}
                  {perGram && <>1 g ≈ {perGram} {currency}</>}
                </ItalicBody>
              )}
            </div>
          );
        })()}

        {/* Notes — same B1 zine bordered block as Your Review.
            Mono caps header, Fraunces italic body. (2026-05-10) */}
        {yarn.notes && (
          <Card>
            <CardSectionHeader>{t.notes}</CardSectionHeader>
            <ItalicBody size="md" style={{ fontSize: 13, padding: '10px 12px 12px' }}>
              {yarn.notes}
            </ItalicBody>
          </Card>
        )}

        {/* Add-another-colorway — B1 zine dashed-ink "create" bar.
            Echoes the dashed-circle CREATE NEW affordance from the
            empty-state pattern; replaces the old orange pill so the
            modal reads consistently end to end. */}
        {onDuplicateColorway && (
          <DashedAddButton onClick={onDuplicateColorway}>
            <span aria-hidden="true" style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>+</span>
            <span>{t.addNewColorway}</span>
          </DashedAddButton>
        )}
        {/* Action bar — B1 zine: black EDIT primary + bordered
            CLOSE secondary + share + delete icon buttons. */}
        <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
          {(onEdit || yarn.id != null) && (
            <ZineButton onClick={onEdit ?? (() => setEditing(true))} data-testid="yarn-edit-button">
              {t.edit}
            </ZineButton>
          )}
          <ZineButton variant="outlined" onClick={onClose} data-testid="yarn-detail-close">
            {t.close}
          </ZineButton>
          {yarn.id != null && (
            <ZineButton
              icon
              variant="outlined"
              type="button"
              onClick={() => setShowShare(true)}
              aria-label={t.shareYarn}
              title={t.shareYarn}
            >
              <Share2 size={16} />
            </ZineButton>
          )}
          {onDelete && (
            <ZineButton icon variant="danger" onClick={onDelete} data-testid="yarn-detail-delete" aria-label={t.delete}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </ZineButton>
          )}
        </div>
      </div>

      {yarn.id != null && (
        <ShareYarnSheet
          yarnId={yarn.id}
          yarnLabel={`${yarn.name}${yarn.colorName ? ` · ${yarn.colorName}` : ''}`}
          open={showShare}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Project summary popup — B1 zine recipe (Veronika, 2026-09-14:
          "it looks unfinished and doesn't correspond to our visual
          language"). Square photo tile, status Chip, bordered Card with
          DetailRows, mono section labels, Chip buttons for yarn / needles,
          solid + outlined ZineButtons. The name is already the modal
          title, so it is not repeated in the body. */}
      {viewProject && (
        <Modal open={true} onClose={() => setViewProject(null)} title={viewProject.name}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="yarn-detail-project-popup">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Card tone="paper-1" style={{ width: 56, height: 56, flex: '0 0 56px', overflow: 'hidden' }}>
                {viewProject.photo ? (
                  <BlobImage src={viewProject.photo} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : null}
              </Card>
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                {viewProject.patternName && <ItalicBody as="span">{viewProject.patternName}</ItalicBody>}
                <Chip>{
                  viewProject.status === 'in-progress' ? t.statusInProgress
                  : viewProject.status === 'hibernating' ? t.statusHibernating
                  : viewProject.status === 'future' ? t.statusFuture
                  : t.statusFinished
                }</Chip>
              </div>
            </div>

            <Card>
              {(() => {
                // Same start chain as the project pages (Veronika
                // 2026-06-12): manual startDate → auto startedAt →
                // createdAt; a 'future' plan shows no Started row.
                const startVal = viewProject.startDate ?? viewProject.startedAt ??
                  (viewProject.status !== 'future' ? viewProject.createdAt : undefined);
                return startVal != null
                  ? <DetailRow label={t.started} value={formatDate(startVal)} />
                  : null;
              })()}
              <DetailRow label={t.lastUpdated} value={formatDate(viewProject.updatedAt)} />
              {viewProject.castOn && viewProject.castOn.stitchCount > 0 && (
                <DetailRow label={t.castOn} value={`${viewProject.castOn.stitchCount} ${t.stitches}${viewProject.castOn.technique ? ` (${viewProject.castOn.technique})` : ''}`} />
              )}
            </Card>

            {viewProject.yarns && viewProject.yarns.length > 0 && (
              <div>
                <MonoLabel size="md" mb={6} style={{ display: 'block' }}>{t.yarn}</MonoLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {viewProject.yarns.map((y, i) => (
                    <Chip key={i} bold onClick={() => openYarnFromChip(y.yarnName)}>
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-block',
                          width: 9,
                          height: 9,
                          marginRight: 6,
                          verticalAlign: '-1px',
                          border: '1px solid var(--ink)',
                          backgroundColor: y.colorHex || 'var(--paper-1)',
                        }}
                      />
                      {y.yarnName}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {viewProject.needles && viewProject.needles.length > 0 && (
              <div>
                <MonoLabel size="md" mb={6} style={{ display: 'block' }}>{t.needles}</MonoLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {viewProject.needles.map((n, i) => (
                    <Chip key={i} bold onClick={() => setNestedNeedle(n)}>
                      {n.size}{n.isCircular ? ' circular' : ''}{n.usedFor ? ` (${n.usedFor})` : ''}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
              <ZineButton
                data-testid="yarn-detail-project-goto"
                onClick={() => { setViewProject(null); onClose(); router.push(`/projects/view?id=${viewProject.id}`); }}
              >
                {t.goToProject}
              </ZineButton>
              <ZineButton variant="outlined" data-testid="yarn-detail-project-close" onClick={() => setViewProject(null)}>
                {t.close}
              </ZineButton>
            </div>
          </div>
        </Modal>
      )}

      {editing && yarn.id != null && (
        <Modal open={true} onClose={() => setEditing(false)} title={`${t.edit} ${t.yarn}`}>
          <YarnForm
            initial={yarn}
            onSubmit={async (data) => {
              await db.yarns.update(yarn.id!, data);
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        </Modal>
      )}
      {nestedYarn && (
        <YarnDetailModal yarn={nestedYarn} onClose={() => setNestedYarn(null)} />
      )}
      {nestedNeedle && (
        <ProjectNeedleDetailModal needle={nestedNeedle} onClose={() => setNestedNeedle(null)} />
      )}

      {lightbox && yarn.photo && (
        <PhotoLightbox
          src={yarn.photo}
          onClose={() => setLightbox(false)}
          closeLabel="Close photo"
        />
      )}
        </div>
      </div>
    </div>
  );
}

